import { BACKEND_API_URL } from '@/lib/constants'

/**
 * Control Plane client-flag transport for the admin console (Account Control
 * Plane P5). Mirrors the web app's adapter: the login page must choose the
 * legacy vs Control Plane flow BEFORE any auth exists, and flip-back must be a
 * server env change rather than a client rebuild — so the two client-facing CP
 * flags are served by the UNAUTHENTICATED `GET /auth/bootstrap-config` endpoint
 * and fetched once at boot, then exposed through synchronous accessors.
 *
 * FAIL-SAFE: any failure (network, timeout, malformed, server flag off, empty
 * host) resolves to DISABLED — the console then takes the legacy path, which is
 * what makes flags-off byte-identical to today.
 */
export interface ControlPlaneClientConfig {
  /** True only when the server flag is on AND a non-empty host was provided. */
  readonly controlPlaneEnabled: boolean
  /** Public base URL of the CP host (login redirect + session RPC target). */
  readonly controlPlaneHost: string
}

const DISABLED: ControlPlaneClientConfig = {
  controlPlaneEnabled: false,
  controlPlaneHost: '',
}

const BOOTSTRAP_FETCH_TIMEOUT_MS = 3_000

let cached: ControlPlaneClientConfig = DISABLED
let loaded = false

function normalizeHost(host: unknown): string {
  if (typeof host !== 'string') return ''
  // Drop a trailing slash so callers can concatenate `${host}/oauth/...`.
  return host.replace(/\/+$/, '')
}

/**
 * Fetch the client CP flags once at boot. Idempotent. MUST be awaited before the
 * app renders so the synchronous accessors are populated.
 */
export async function loadControlPlaneConfig(): Promise<ControlPlaneClientConfig> {
  if (loaded) return cached
  const controller = new AbortController()
  const timer = setTimeout(() => controller.abort(), BOOTSTRAP_FETCH_TIMEOUT_MS)
  try {
    const res = await fetch(`${BACKEND_API_URL}/auth/bootstrap-config`, {
      // The endpoint is unauthenticated and public; no cookie needed.
      credentials: 'omit',
      headers: { accept: 'application/json' },
      signal: controller.signal,
    })
    if (res.ok) {
      const data = (await res.json()) as {
        controlPlaneEnabled?: unknown
        controlPlaneHost?: unknown
      }
      const host = normalizeHost(data.controlPlaneHost)
      cached =
        data.controlPlaneEnabled === true && host.length > 0
          ? { controlPlaneEnabled: true, controlPlaneHost: host }
          : DISABLED
    }
  } catch {
    // Fail-safe to legacy: never enter the CP path on an unreadable flag.
    cached = DISABLED
  } finally {
    clearTimeout(timer)
    loaded = true
  }
  return cached
}

export function isControlPlaneEnabled(): boolean {
  return cached.controlPlaneEnabled
}

export function getControlPlaneHost(): string {
  return cached.controlPlaneHost
}
